<template>
  <div class="m-page">
    <div class="search-box">
      <van-search
        v-model="keyword"
        shape="round"
        placeholder="代码或名称，如 300308"
        show-action
        @search="queryKline"
        @clear="clearResult"
      >
        <template #action>
          <div @click="queryKline">查询</div>
        </template>
      </van-search>
      <van-cell-group v-if="suggestions.length" inset class="suggest-box">
        <van-cell
          v-for="item in suggestions"
          :key="item.code"
          :title="item.code"
          :label="`${item.name || ''} ${item.industry || ''}`"
          is-link
          @click="pickStock(item)"
        />
      </van-cell-group>
    </div>

    <div v-if="stockMeta" class="meta">
      <a :href="stockstarCodeLink(stockMeta.code)" target="_blank" rel="noopener noreferrer">{{ stockMeta.code }}</a>
      <a :href="eastmoneyCodeLink(stockMeta)" target="_blank" rel="noopener noreferrer">{{ stockMeta.name }}</a>
      <span class="muted">{{ stockMeta.industry || '' }} · {{ bars.length }} 条</span>
    </div>

    <van-empty v-if="error" :description="error" />
    <van-loading v-else-if="loading" class="page-loading" vertical>查询中...</van-loading>
    <van-empty v-else-if="queried && !bars.length" description="无数据" />

    <div v-else class="bar-list">
      <div v-for="row in bars" :key="row.trade_date" class="bar-card">
        <div class="bar-date">{{ row.trade_date }}</div>
        <div class="bar-grid">
          <span>开 {{ formatPrice(row.open) }}</span>
          <span>高 {{ formatPrice(row.high) }}</span>
          <span>低 {{ formatPrice(row.low) }}</span>
          <span>收 {{ formatPrice(row.close) }}</span>
        </div>
        <div class="bar-row">
          <span :class="pctClass(row.pct_chg)">{{ formatPct(row.pct_chg) }}%</span>
          <span class="muted">换手 {{ formatPct(row.turnover) }}%</span>
          <span class="muted">量 {{ row.volume || '—' }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchWithCredentials } from '../utils/http.js'
import { eastmoneyCodeLink, stockstarCodeLink } from '../utils/stockLink.js'

export default {
  name: 'MobileKline',
  data() {
    return {
      keyword: '300308',
      selectedCode: '300308',
      suggestions: [],
      searchTimer: null,
      bars: [],
      stockMeta: null,
      loading: false,
      queried: false,
      error: '',
    }
  },
  watch: {
    keyword(val) {
      clearTimeout(this.searchTimer)
      const q = String(val || '').trim()
      if (!q || q.length < 1) {
        this.suggestions = []
        return
      }
      this.searchTimer = setTimeout(() => this.searchStock(q), 220)
    },
  },
  created() {
    this.queryKline()
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer)
  },
  methods: {
    eastmoneyCodeLink,
    stockstarCodeLink,
    async searchStock(q) {
      try {
        const { res, data } = await fetchWithCredentials(`/api/search?q=${encodeURIComponent(q)}`)
        if (!res.ok) {
          this.suggestions = []
          return
        }
        this.suggestions = (data.rows || []).slice(0, 8).map((row) => ({
          code: row.code,
          name: row.name,
          industry: row.industry,
        }))
      } catch {
        this.suggestions = []
      }
    },
    pickStock(item) {
      this.selectedCode = item.code
      this.keyword = `${item.code} ${item.name || ''}`.trim()
      this.suggestions = []
      this.queryKline()
    },
    resolveCode() {
      if (this.selectedCode) return this.selectedCode
      const m = String(this.keyword || '').match(/\d{6}/)
      return m ? m[0] : ''
    },
    async queryKline() {
      const code = this.resolveCode()
      if (!code) {
        this.error = '请输入股票代码'
        return
      }
      this.loading = true
      this.error = ''
      this.queried = true
      try {
        const params = new URLSearchParams({ code })
        const { res, data } = await fetchWithCredentials(`/api/kline?${params}`)
        if (!res.ok) throw new Error(data.error || `查询失败 ${res.status}`)
        this.stockMeta = data.stock || { code: data.code, name: data.name, industry: data.industry }
        this.selectedCode = code
        this.bars = (data.bars || []).slice().sort((a, b) => String(b.trade_date).localeCompare(String(a.trade_date)))
      } catch (e) {
        this.bars = []
        this.stockMeta = null
        this.error = e.message || String(e)
      } finally {
        this.loading = false
      }
    },
    clearResult() {
      this.suggestions = []
    },
    formatPrice(v) {
      const n = Number(v)
      return Number.isFinite(n) ? n.toFixed(2) : '—'
    },
    formatPct(v) {
      if (v == null || v === '') return '—'
      const n = Number(v)
      return Number.isFinite(n) ? n.toFixed(2) : '—'
    },
    pctClass(v) {
      const n = Number(v)
      if (!Number.isFinite(n) || n === 0) return 'muted'
      return n > 0 ? 'up' : 'down'
    },
  },
}
</script>

<style scoped>
.m-page { padding-bottom: 12px; }
.suggest-box { margin-top: 4px; }
.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 16px 8px;
  font-size: 13px;
}
.meta a { color: #1989fa; text-decoration: none; }
.page-loading { padding: 48px 0; }
.bar-card {
  margin: 8px 12px;
  padding: 10px 12px;
  background: #fff;
  border-radius: 10px;
}
.bar-date { font-weight: 600; font-size: 13px; margin-bottom: 6px; }
.bar-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 10px;
  font-size: 12px;
  color: #646566;
}
.bar-row {
  display: flex;
  gap: 12px;
  margin-top: 6px;
  font-size: 12px;
}
.up { color: #ee0a24; }
.down { color: #07c160; }
.muted { color: #969799; }
</style>
